import { useState, useEffect } from 'react'
import { useAppDispatch } from '../../../../../store/hooks'
import { setAnnotations as setGlobalAnnotations } from '../../../../../store/slices/documentsSlice'
import { functionsClient } from '../../../../../lib/functionsClient'
import { buildSemanticExtraction } from '../../../../ai-viewer/utils/extraction-grouping'
import type { Annotation } from '../../../../ai-viewer/eventing/types'
import type { SemanticExtraction, RawExtractionData } from '../../../../ai-viewer/eventing/extraction-types'
import type { WorkItem } from '../../../../../store/slices/workSlice'

interface UseDocumentLoaderParams {
  documentId?: string
  workItem: WorkItem | null
  localFileUrl?: string
}

interface UseDocumentLoaderResult {
  documentUrl: string | null
  semanticExtraction: SemanticExtraction | null
  loading: boolean
  error: string | null
}

export function useDocumentLoader({
  documentId,
  workItem,
  localFileUrl
}: UseDocumentLoaderParams): UseDocumentLoaderResult {
  const dispatch = useAppDispatch()
  const [documentUrl, setDocumentUrl] = useState<string | null>(null)
  const [semanticExtraction, setSemanticExtraction] = useState<SemanticExtraction | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Use local file URL immediately if available
  useEffect(() => {
    if (localFileUrl) {
      setDocumentUrl(localFileUrl)
      setLoading(false)
    }
  }, [localFileUrl])

  // Load document and extraction data
  useEffect(() => {
    let isActive = true

    const loadDocument = async () => {
      try {
        if (!isActive) return
        setLoading(true)
        setError(null)

        // Get document URL from backend (skip if we have localFileUrl)
        if (!localFileUrl && documentId) {
          const docResult = await functionsClient.getDocument(documentId)
          if (!isActive) return
          if (!docResult.success) {
            throw new Error('Failed to load document')
          }
          setDocumentUrl(docResult.url)
        } else if (!localFileUrl && !documentId) {
          // If no documentId and no localFileUrl, we can't load the document
          throw new Error('No document URL or documentId provided')
        }

        // Load annotations AND extraction data together
        if (workItem?.id) {
          try {
            const { getFirestore, collection, getDocs } = await import('firebase/firestore')
            const db = getFirestore()
            
            // STEP 1: Load annotations from Firestore and dispatch to Redux
            const annotationsRef = collection(db, 'work', workItem.id, 'annotations')
            const annotationsSnapshot = await getDocs(annotationsRef)
            
            let loadedAnnotations: Annotation[] = []
            if (!annotationsSnapshot.empty) {
              loadedAnnotations = annotationsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
              })) as Annotation[]
              
              // Dispatch to Redux (single source of truth)
              dispatch(setGlobalAnnotations(loadedAnnotations))
            } else {
              console.warn('⚠️ No annotations found in subcollection')
              // Clear annotations in Redux
              dispatch(setGlobalAnnotations([]))
            }

            // STEP 2: Get extraction data from work item outputs
            const rawExtractionData: RawExtractionData | null = workItem.outputs?.extractedData || null
            
            if (!rawExtractionData) {
              console.warn('⚠️ No extraction data in work item outputs')
            }
            
            // STEP 3: Build semantic extraction
            if (rawExtractionData) {
              const semantic = buildSemanticExtraction(
                workItem.id,
                documentId,
                rawExtractionData,
                loadedAnnotations
              )
              setSemanticExtraction(semantic)
            } else {
              console.error('❌ Cannot build semantic extraction - no extraction data', {
                hasRawData: !!rawExtractionData,
                annotationCount: loadedAnnotations.length
              })
            }
          } catch (error) {
            console.error('❌ Error loading data:', error)
          }
        }
      } catch (err: any) {
        console.error('Error loading document:', err)
        if (isActive) {
          setError(err.message || 'Failed to load document')
        }
      } finally {
        if (isActive) {
          setLoading(false)
        }
      }
    }

    // Only load if we have a localFileUrl or a valid documentId
    if (localFileUrl || (documentId && documentId !== '__DEV_TEST__' && !documentId.startsWith('temp-'))) {
      loadDocument()
    } else {
      // If we have neither, set loading to false
      setLoading(false)
    }
    
    return () => {
      isActive = false
    }
  }, [documentId, workItem?.id, workItem?.outputs?.extractedData, localFileUrl, dispatch])

  return {
    documentUrl,
    semanticExtraction,
    loading,
    error
  }
}

